/**
 * advanceModel.js - 2026-10-21 (R2-30, docs/TODO_CURRENT.md; Dr. Tatar: advance per brigade per phase and the Role 2 sawtooth; research: docs/research/RESEARCH_ADVANCE_RATES.md)
 *
 * ADVANCE. Each brigade has an advance in km per day in each phase. The planner may type it (phase.advance_km_day); empty = a default from the form of
 * battle, the enemy resistance, the maneuver group and the special condition. The front itself is not drawn: only the distance the medical chain falls behind.
 *
 * SAWTOOTH OF THE LEGS. Role 1 moves with the battalions (it stays at the same distance from the front). The Role 2 Basic (brigade medical company) stays where
 * it is until it repositions, so the Role 1 -> Role 2B leg grows by the km advanced since its last jump. When the growth reaches JUMP_TRIGGER_KM.R2B (and at
 * least MIN_DAYS_BETWEEN_JUMPS.R2B days have passed) the Role 2B jumps to the Role 1: on that day it is packed (the existing displacement ramp of
 * casualtyCalculatorConstants closes it for its set-up time) and the leg is short again the next day. The same for the Role 2 Enhanced behind it: the
 * Role 2B -> Role 2E leg grows by what the Role 2B has moved forward, and the Role 2E jumps at JUMP_TRIGGER_KM.R2E. Role 3 stays. When the advance stops the
 * units catch up (CATCHUP_KM). Leg growth costs minutes at the speed the existing evacuation chain uses: 4 min per km on the Role 1 -> Role 2B leg and
 * 3 min per km on the Role 2B -> surgery leg (casualtyCalculator calculateCumulativeTimeToSurgery), divided by the ground speed factor of the day.
 *
 * Status of the numbers: the default advance table is EDUCATED GUESS anchored on the research record (Dupuy division engagements 1.8 to 4.5 km/day, penetration
 * 4 to 11 km; Leiden 2 km/day heavily engaged to 11 lightly engaged; Soviet planning 10 to 15 km/day through the tactical zone, 25 to 50 in the operational
 * depth; Iraq 2003 about 26 km/day; urban fighting one half to one third of the advance); the jump triggers and minimum days are EDUCATED GUESS (no study).
 */
export const ADVANCE_LEGS_ON = true;   // false = no advance, no leg growth, the displacement interval of evacuationProfiles.js only (as before)

/** km per day by form of battle at light / moderate / heavy enemy resistance. Forms not listed: no advance. EDUCATED GUESS. */
export const ADVANCE_KM_PER_DAY = Object.freeze({
  MOVEMENT_TO_CONTACT: { LIGHT: 15, MODERATE: 8, HEAVY: 3 },
  HASTY_ATTACK:        { LIGHT: 12, MODERATE: 6, HEAVY: 2 },
  DELIBERATE_ATTACK:   { LIGHT: 8, MODERATE: 4, HEAVY: 1.5 },
  EXPLOITATION:        { LIGHT: 35, MODERATE: 20, HEAVY: 8 },
  PURSUIT:             { LIGHT: 40, MODERATE: 25, HEAVY: 10 }
});
/** Multiplier of the advance by maneuver group (a fixing or feint brigade does not advance). EDUCATED GUESS. */
export const ADVANCE_GROUP_FACTOR = Object.freeze({ FRONTAL: 1, FLANK: 1, LIMITED_OBJECTIVE: 0.5, FEINT_FIXING: 0 });
/** Urban: one half to one third of the advance (Hue 1968 reading, phaseCasualtyTable). Breach: one lane. EDUCATED GUESS. */
export const ADVANCE_CONDITION_FACTOR = Object.freeze({ URBAN: 0.4, BREACH: 0.5, FORTIFIED_ASSAULT: 0.6 });

/** The Role 1 -> Role 2B leg may grow by this many km before the Role 2B jumps; the Role 2B -> Role 2E leg likewise. EDUCATED GUESS. */
export const JUMP_TRIGGER_KM = Object.freeze({ R2B: 15, R2E: 60 });
/** When the advance has stopped the units catch up once the leg is this long. EDUCATED GUESS. */
export const CATCHUP_KM = Object.freeze({ R2B: 5, R2E: 20 });
/** A unit that has just jumped cannot jump again before this many days (pack, move, unpack: casualtyCalculatorConstants set-up times). EDUCATED GUESS. */
export const MIN_DAYS_BETWEEN_JUMPS = Object.freeze({ R2B: 2, R2E: 3 });
/** Minutes per km of the existing evacuation chain (casualtyCalculator calculateCumulativeTimeToSurgery). */
export const MIN_PER_KM = Object.freeze({ LEG1_R1_TO_R2B: 4, LEG2_R2B_TO_SURGERY: 3 });

export const ADVANCE_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Default advance table anchored on docs/research/RESEARCH_ADVANCE_RATES.md; jump triggers and minimum days are guesses; leg minutes per km are the existing chain values.'
});

/**
 * Advance of one brigade on one day of a phase (km).
 * @param {Object} c form (PHASE_FORMS key), resistanceLevel (LIGHT / MODERATE / HEAVY), group, condition, dayInPhase, footholdDay, plannerKm (typed, empty = default)
 */
export function phaseAdvanceKm({ form, resistanceLevel, group, condition, dayInPhase, footholdDay, plannerKm }) {
  if (plannerKm !== undefined && plannerKm !== null && plannerKm !== '') {
    if (typeof plannerKm !== 'number' || Number.isNaN(plannerKm) || plannerKm < 0) throw new Error(`advanceModel: advance km per day must be a number >= 0 (got: ${plannerKm})`);
    return plannerKm;
  }
  const row = ADVANCE_KM_PER_DAY[form];
  if (!row) return 0;
  if (!Object.prototype.hasOwnProperty.call(row, resistanceLevel)) throw new Error(`advanceModel: unknown resistance level '${resistanceLevel}' (valid: LIGHT, MODERATE, HEAVY)`);
  let km = row[resistanceLevel];
  if (group !== undefined && group !== null) {
    if (!Object.prototype.hasOwnProperty.call(ADVANCE_GROUP_FACTOR, group)) throw new Error(`advanceModel: unknown maneuver group '${group}'`);
    km *= ADVANCE_GROUP_FACTOR[group];
  }
  if (condition === 'RIVER_ASSAULT' || condition === 'AMPHIBIOUS') {
    // no advance until the foothold is across the water; then the normal advance
    const foothold = typeof footholdDay === 'number' ? footholdDay : (condition === 'RIVER_ASSAULT' ? 2 : 3);
    if (dayInPhase < foothold) km = 0;
  } else if (condition === 'AIRBORNE') {
    const linkUp = typeof footholdDay === 'number' ? footholdDay : 3;
    if (dayInPhase < linkUp) km = 0;
  } else if (condition && Object.prototype.hasOwnProperty.call(ADVANCE_CONDITION_FACTOR, condition)) {
    km *= ADVANCE_CONDITION_FACTOR[condition];
  }
  return km;
}

/** Positions along the axis of advance (km), days since each unit last jumped. */
export function createAxisState() {
  return { front_km: 0, r2b_km: 0, r2e_km: 0, days_since_r2b_jump: Infinity, days_since_r2e_jump: Infinity };
}

/**
 * One day. Leg growth of the day is the growth BEFORE the jump of that day (casualties of the day are evacuated on the long legs; the unit that packs is
 * closed by the displacement ramp). Mutates the state.
 * @returns {{advance_km, front_km, leg1_extra_km, leg2_extra_km, r2b_jump, r2e_jump, active}}
 */
export function stepAxis(state, advanceKm) {
  if (typeof advanceKm !== 'number' || Number.isNaN(advanceKm) || advanceKm < 0) throw new Error(`advanceModel.stepAxis: advanceKm must be a number >= 0 (got: ${advanceKm})`);
  state.front_km += advanceKm;
  state.days_since_r2b_jump += 1;
  state.days_since_r2e_jump += 1;
  const leg1 = state.front_km - state.r2b_km;
  const leg2 = state.r2b_km - state.r2e_km;
  const moving = advanceKm > 0;
  const r2bJump = leg1 >= (moving ? JUMP_TRIGGER_KM.R2B : CATCHUP_KM.R2B) && state.days_since_r2b_jump >= MIN_DAYS_BETWEEN_JUMPS.R2B;
  if (r2bJump) { state.r2b_km = state.front_km; state.days_since_r2b_jump = 0; }
  const r2eJump = leg2 >= (moving ? JUMP_TRIGGER_KM.R2E : CATCHUP_KM.R2E) && state.days_since_r2e_jump >= MIN_DAYS_BETWEEN_JUMPS.R2E;
  if (r2eJump) { state.r2e_km = state.r2b_km; state.days_since_r2e_jump = 0; }
  return { advance_km: advanceKm, front_km: state.front_km, leg1_extra_km: leg1, leg2_extra_km: leg2, r2b_jump: r2bJump, r2e_jump: r2eJump, active: moving || leg1 > 0 || leg2 > 0 };
}

/** Minutes the grown legs add: arrival (Role 1 -> Role 2B) and, when surgery is not at the Role 2B, surgery (Role 2B -> surgical facility). */
export function legGrowthMinutes({ leg1ExtraKm, leg2ExtraKm, surgeryAtR2B, groundSpeedFactor }) {
  if (typeof groundSpeedFactor !== 'number' || !(groundSpeedFactor > 0)) throw new Error(`advanceModel.legGrowthMinutes: groundSpeedFactor must be > 0 (got: ${groundSpeedFactor})`);
  return {
    extra_arrival_min: leg1ExtraKm * MIN_PER_KM.LEG1_R1_TO_R2B / groundSpeedFactor,
    extra_surgery_min: surgeryAtR2B ? 0 : leg2ExtraKm * MIN_PER_KM.LEG2_R2B_TO_SURGERY / groundSpeedFactor
  };
}
